/**
 * usePlacementZoneBoxes -- composable for fetching the per-(SKU, view)
 * placement-zone bounding boxes the Stage.vue overlay renders against.
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase F F3.c -- catalog-driven placement-zone overlay.
 *
 * Public surface:
 *
 *     const { boxes, loading, error, fetch } = usePlacementZoneBoxes()
 *     await fetch('18000')
 *     // boxes.value === [{ view_code, group_name, image_width_px,
 *     //                    image_height_px, x_px, y_px, w_px, h_px,
 *     //                    source }, ...]
 *
 * Backend contract: GET /api/placement_zone_boxes/?supplier_sku=<sku>
 * returns { data: Box[] }. An unknown SKU returns { data: [] } with
 * HTTP 200 (NOT 404) -- the empty list is a legitimate non-error
 * meaning "no overlay available for this SKU."
 *
 * Behavior notes:
 *   - Concurrent fetches are de-duped via AbortController + a monotonic
 *     generation counter; late stale responses are discarded.
 *   - `boxes` is cleared synchronously when fetch() starts with a new
 *     SKU so the overlay hides cleanly during the in-flight gap.
 *   - SKU validation mirrors useSanmarProductMeta -- ^[A-Za-z0-9\-]{2,32}$.
 */
import { ref } from 'vue'
import axiosInstance from '@/axios'

const SKU_RE = /^[A-Za-z0-9-]{2,32}$/

export function usePlacementZoneBoxes() {
    const boxes = ref([])
    const loading = ref(false)
    const error = ref(null)

    let generation = 0
    let currentAbort = null
    let currentSku = null

    const fetch = async (sku) => {
        if (typeof sku !== 'string' || !SKU_RE.test(sku)) {
            // Treat unknown / malformed SKU the same as "no data" --
            // hide the overlay rather than surface an error. The
            // Stage.vue overlay's contract is "render only when we
            // have catalog data."
            //
            // Also: cancel any in-flight prior request AND bump the
            // generation counter. Without this, a stale resolve from a
            // previous fetch lands after the reset and re-populates
            // boxes for the wrong product (the user cleared the input
            // but sees boxes briefly anyway). gen guard catches it now.
            if (currentAbort) {
                currentAbort.abort()
                currentAbort = null
            }
            ++generation
            boxes.value = []
            loading.value = false
            error.value = null
            currentSku = null
            return
        }

        if (currentAbort) {
            currentAbort.abort()
        }

        const myGeneration = ++generation
        const controller =
            typeof AbortController !== 'undefined'
                ? new AbortController()
                : null
        currentAbort = controller

        // Same-SKU refetch keeps the prior boxes visible while loading
        // (no flicker). Different-SKU refetch clears immediately so the
        // overlay doesn't briefly render the previous product's box.
        if (sku !== currentSku) {
            boxes.value = []
        }
        currentSku = sku
        loading.value = true
        error.value = null

        try {
            const response = await axiosInstance.get(
                `/api/placement_zone_boxes/`,
                {
                    params: { supplier_sku: sku },
                    signal: controller ? controller.signal : undefined,
                },
            )

            if (myGeneration !== generation) return

            const data = response?.data?.data
            boxes.value = Array.isArray(data) ? data : []
            error.value = null
        } catch (err) {
            if (myGeneration !== generation) return

            const isAbort =
                err?.name === 'CanceledError' ||
                err?.name === 'AbortError' ||
                err?.code === 'ERR_CANCELED'
            if (isAbort) return

            error.value = {
                status: err?.response?.status ?? 0,
                message: 'Failed to load placement zone boxes.',
            }
            boxes.value = []
        } finally {
            if (myGeneration === generation) {
                loading.value = false
                currentAbort = null
            }
        }
    }

    return { boxes, loading, error, fetch }
}
